import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Crown, Gamepad2, Trophy, LogOut } from "lucide-react";
import Seo from "@/components/Seo";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { GAMES } from "@/data/games";
import { Button } from "@/components/ui/button";

export default function Profile() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [scores, setScores] = useState([]);

  useEffect(() => {
    if (user === false) navigate("/");
  }, [user, navigate]);

  useEffect(() => {
    async function load() {
      if (!user) return;
      const results = await Promise.all(
        GAMES.filter((g) => g.type === "single").map(async (g) => {
          try {
            const { data } = await api.get(`/scores/${g.id}`);
            const mine = data.find((r) => r.user_id === user.user_id);
            return { game: g.title, score: mine ? mine.score : null };
          } catch { return { game: g.title, score: null }; }
        })
      );
      setScores(results);
    }
    load();
  }, [user]);

  if (!user) return null;

  return (
    <div className="max-w-3xl mx-auto px-4 md:px-8 py-12" data-testid="profile-page">
      <Seo title="My Profile" description="Your Video Game Mania profile and high scores." />

      <div className="flex items-center gap-5 mb-10">
        {user.picture ? (
          <img src={user.picture} alt="" className="w-20 h-20 rounded-full border-2 border-neon-cyan" />
        ) : (
          <div className="w-20 h-20 rounded-full bg-neon-cyan text-black flex items-center justify-center font-display font-black text-3xl">
            {(user.name || user.email)[0].toUpperCase()}
          </div>
        )}
        <div>
          <h1 className="font-display font-black text-3xl uppercase tracking-tighter flex items-center gap-2">
            {user.name || user.email.split("@")[0]}
            {user.is_premium && <Crown className="w-6 h-6 text-neon-yellow" />}
          </h1>
          <p className="text-zinc-400">{user.email}</p>
          <span className={`inline-block mt-1 text-xs font-bold uppercase tracking-widest px-2 py-0.5 rounded ${
            user.is_premium ? "bg-neon-yellow text-black" : "bg-zinc-800 text-zinc-400"}`}>
            {user.is_premium ? "Premium Member" : "Free Player"}
          </span>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-4 mb-10">
        <Button onClick={() => navigate("/arcade")} className="bg-neon-cyan text-black rounded-none font-bold uppercase tracking-widest h-12">
          <Gamepad2 className="w-4 h-4 mr-2" /> Play Games
        </Button>
        {!user.is_premium && (
          <Button onClick={() => navigate("/subscribe")} className="bg-neon-pink text-white rounded-none font-bold uppercase tracking-widest h-12 hover:bg-white hover:text-black">
            <Crown className="w-4 h-4 mr-2" /> Upgrade to Premium
          </Button>
        )}
      </div>

      <h2 className="font-display font-bold text-xl uppercase tracking-tight mb-4 flex items-center gap-2">
        <Trophy className="w-5 h-5 text-neon-yellow" /> Your High Scores
      </h2>
      <div className="space-y-2">
        {scores.map((s, i) => (
          <div key={i} className="flex items-center justify-between px-4 py-3 bg-zinc-950 border-2 border-zinc-800 rounded-lg" data-testid={`profile-score-${i}`}>
            <span className="font-semibold">{s.game}</span>
            <span className="font-mono text-neon-cyan">{s.score != null ? s.score : "—"}</span>
          </div>
        ))}
      </div>

      <button onClick={logout} className="mt-10 inline-flex items-center gap-2 text-neon-pink font-semibold uppercase text-sm tracking-widest hover:underline" data-testid="profile-logout">
        <LogOut className="w-4 h-4" /> Log out
      </button>
    </div>
  );
}
